import { useMemo, useState } from 'react';
import { IQuestionnaire } from 'src/shared/types/Case';
import QuestionnaireCard, { QuestionnaireStatus } from 'src/components/QuestionnaireCard/QuestionnaireCard';
import GlobalCaseTransitionModal, {
  GlobalCaseTransitionModalType,
} from '../GlobalCaseTransitionModal/GlobalCaseTransitionModal';
import { shouldSkipGlobalCaseTransitionModal } from 'src/utils/services/case';
import { getRemoteInstanceRedirectURL } from 'src/utils/services/common';
import styles from './Questionnaires.module.scss';
import { AIDI_SUPPORTED_QUESTIONNAIRE_NAMES } from 'src/utils/constants/common';
import { AidiProgressStatus, useAidiAccess } from 'src/utils/hooks/useAidiAccess';
import { getAidiCasePath, getQStatusFromQuestionnaire } from 'src/utils/services/aidiQuestionnaire';
import { useNavigateWithSecureParams } from 'src/utils/hooks/useNavigateWithSecureParams';
import { useFlags } from 'launchdarkly-react-client-sdk';

const Questionnaires = ({
  caseId,
  remoteCaseId,
  questionnaires,
  outline,
  isRemote = false,
}: {
  caseId: number;
  remoteCaseId?: number;
  questionnaires: IQuestionnaire[];
  outline?: boolean;
  isRemote?: boolean;
}) => {
  const [selectedQuestionnaires, setSelectedQuestionnaires] = useState<IQuestionnaire[] | null>(null);
  const { navigate } = useNavigateWithSecureParams();
  const flags = useFlags();
  const useUrlCaseId = flags['fnx-show-backtodocuments'] ?? false;

  // Local (non-remote) cases may have an AIDI-eligible questionnaire (e.g. USA - H-1B Employee
  // Questionnaire) assigned alongside Legacy-only ones. Even though this list is reached via the
  // Legacy case-documents page, clicking that specific questionnaire must still route the user back
  // into the AIDI experience instead of the old IMS questionnaire form, so users can't bypass AIDI
  // routing just by navigating to /case-documents directly.
  const { shouldShowAidiEntry, aidiProgressStatus } = useAidiAccess({
    caseId: !isRemote ? caseId : null,
    aidiStatus: true,
  });

  const isAidiEligibleGroup = (group: IQuestionnaire[]) =>
    !isRemote && shouldShowAidiEntry && group.some((q) => AIDI_SUPPORTED_QUESTIONNAIRE_NAMES.includes(q.questionnaire));

  const goToAidiQuestionnaire = (group: IQuestionnaire[]) => {
    const primary = group[0];
    const qStatus = getQStatusFromQuestionnaire(primary);
    // See CaseStage.tsx for why an IDLE form status is upgraded to IN_PROGRESS when AIDI already
    // has progress recorded for the case.
    const effectiveQStatus =
      qStatus === AidiProgressStatus.IDLE && aidiProgressStatus === AidiProgressStatus.IN_PROGRESS
        ? AidiProgressStatus.IN_PROGRESS
        : qStatus;
    const path = getAidiCasePath(caseId, effectiveQStatus, useUrlCaseId);
    navigate(path, { secureParams: { caseId, questionnaireId: primary.caseQuestionnaireId } });
  };

  const goToQuestionnaire = (questionnaires: IQuestionnaire[]) => {
    if (!questionnaires.length) return;

    if (isAidiEligibleGroup(questionnaires)) {
      goToAidiQuestionnaire(questionnaires);
      return;
    }

    if (isRemote) {
      if (shouldSkipGlobalCaseTransitionModal()) {
        redirectToRemoteQuestionnaire(questionnaires);
      } else {
        setSelectedQuestionnaires(questionnaires);
      }
    } else {
      window.location.href = `${window.FN_CONFIG.IMS_UI_URL}${getRedirectToQuestionnairePath(questionnaires, caseId)}`;
    }
  };

  const redirectToRemoteQuestionnaire = (questionnaires: IQuestionnaire[]) => {
    if (!questionnaires.length || !remoteCaseId) return;

    const targetUrl = `${window.FN_CONFIG.CROSS_INSTANCE_URL}${getRedirectToQuestionnairePath(questionnaires, remoteCaseId)}`;
    window.open(getRemoteInstanceRedirectURL(targetUrl), '_blank');
  };

  const getRedirectToQuestionnairePath = (questionnaires: IQuestionnaire[], targetCaseId: number) => {
    const caseQuestionnaireIds = questionnaires.map((q) => q.caseQuestionnaireId);
    const ids = encodeURIComponent(`[${caseQuestionnaireIds.join(',')}]`);
    return `/#/dashboard/casequestionnaire/${ids}/${targetCaseId}?fnRedirect=true`;
  };

  const groupedQuestionnaires = useMemo(() => {
    if (!questionnaires?.length) return [];

    const beneficiaryQuestionnaires = questionnaires.reduce<Record<string, IQuestionnaire[]>>((acc, item) => {
      if (!acc[item.questionnaire]) {
        acc[item.questionnaire] = [];
      }
      acc[item.questionnaire].push(item);
      return acc;
    }, {});

    return Object.values(beneficiaryQuestionnaires);
  }, [questionnaires]);

  const groupQuestionnaireStatus = (questionnaires: IQuestionnaire[]) => {
    let notStartedCount = 0;
    let completeCount = 0;
    const length = questionnaires.length;

    for (let index = 0; index < length; index++) {
      const status = questionnaires[index].questionnaireStatus;
      if (status.indexOf(QuestionnaireStatus.NotStarted) != -1) notStartedCount++;
      if (status.indexOf(QuestionnaireStatus.Completed) != -1) completeCount++;
    }

    if (notStartedCount === length) return QuestionnaireStatus.NotStarted;

    if (completeCount === length) return QuestionnaireStatus.Completed;

    return QuestionnaireStatus.InProgress;
  };

  const getEarliestAssignDate = (group: IQuestionnaire[]): number => {
    return group.reduce((earliest, q) => {
      const t = new Date(q.assignDate).getTime();
      return t < earliest ? t : earliest;
    }, Infinity);
  };

  const sortedGroups = useMemo(() => {
    const newGroups = [...groupedQuestionnaires];

    newGroups.sort((groupA, groupB) => {
      const statusA = groupQuestionnaireStatus(groupA);
      const statusB = groupQuestionnaireStatus(groupB);

      // Show incomplete questionnaires first
      const isCompletedA = statusA === QuestionnaireStatus.Completed;
      const isCompletedB = statusB === QuestionnaireStatus.Completed;

      if (isCompletedA && !isCompletedB) return 1;
      if (!isCompletedA && isCompletedB) return -1;

      // Sort by earliest assign date
      const earliestA = getEarliestAssignDate(groupA);
      const earliestB = getEarliestAssignDate(groupB);

      return earliestA - earliestB;
    });

    return newGroups;
  }, [groupedQuestionnaires]);

  return (
    <>
      <div className={styles['questionnaires-container']}>
        {sortedGroups.map((questionnaires: IQuestionnaire[], index: number) => {
          const status = groupQuestionnaireStatus(questionnaires);
          return (
            <QuestionnaireCard
              key={index}
              outline={outline}
              name={questionnaires[0]?.questionnaire}
              relationships={questionnaires.map((questionnaire) => questionnaire.relationship)}
              status={status}
              completedDate={questionnaires[0]?.updatedDate}
              onClick={() => goToQuestionnaire(questionnaires)}
            />
          );
        })}
      </div>
      <GlobalCaseTransitionModal
        isModalOpen={!!selectedQuestionnaires}
        modalType={GlobalCaseTransitionModalType.QUESTIONNAIRE}
        name={selectedQuestionnaires?.[0]?.questionnaire || ''}
        onModalClose={() => setSelectedQuestionnaires(null)}
        onNavigate={() => redirectToRemoteQuestionnaire(selectedQuestionnaires || [])}
      />
    </>
  );
};

export default Questionnaires;
